import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { api, sendAsync } from "../../../app/applicationSlice";
import { RootState, RootThunk, RootThunkApi } from "../../../app/store";
import * as D from "../../../models/department";
import { Department } from "../../../models/department";
import * as F from "../../../models/fee";
import { Fee } from "../../../models/fee";
import { DefaultPageSize } from "../../../models/pagination";
import * as P from "../../../models/patient";
import { Patient } from "../../../models/patient";
import * as U from "../../../models/user";
import { User } from "../../../models/user";
import { getOutpatientDepartment as apiGetOutpatientDepartment } from "../../../services/department";
import {
  getPatientDetail as apiGetPatientDetail,
  getPatientList as apiGetPatientList,
} from "../../../services/patient";
import {
  getRegistrationFeeList as apiGetRegistrationFeeList,
  register as apiRegister,
  RegisterParams,
} from "../../../services/registration";
import { getDoctorsInDepartment as apiGetDoctorsInDepartment } from "../../../services/user";

interface RegisterState {
  departments: Department[];
  doctors: User[];
  patients: Patient[];
  patient?: Patient;
  feeList: Fee[];
}

const initialState: RegisterState = {
  departments: [],
  doctors: [],
  patients: [],
  patient: undefined,
  feeList: [],
};

export const register = createAsyncThunk<
  string,
  RegisterParams,
  RootThunkApi<string>
>("register/registerStatus", async (params, api) => {
  return sendAsync(
    apiRegister({
      ...params,
      registration: {
        ...params.registration,
        patientName: params.patient.name,
        patientPhone: params.patient.phone,
      },
    }),
    api
  );
});

const registerSlice = createSlice({
  name: "register",
  initialState,
  reducers: {
    setDepartments: (state, action: PayloadAction<Department[]>) => {
      state.departments = action.payload;
    },
    setDoctors: (state, action: PayloadAction<User[]>) => {
      state.doctors = action.payload;
    },
    setPatients: (state, action: PayloadAction<Patient[]>) => {
      state.patients = action.payload;
    },
    setPatient: (state, action: PayloadAction<Patient | undefined>) => {
      state.patient = action.payload;
    },
    setFeeList: (state, action: PayloadAction<Fee[]>) => {
      state.feeList = action.payload;
    },
  },
});

const {
  setDepartments,
  setPatients,
  setFeeList: setRegistrationFeeList,
} = registerSlice.actions;

export const { setDoctors, setPatient } = registerSlice.actions;

export const selectDepartments = (state: RootState): Department[] =>
  state.register.departments;

export const selectDoctors = (state: RootState): User[] =>
  state.register.doctors;

export const selectPatients = (state: RootState): Patient[] =>
  state.register.patients;

export const selectPatientDetail = (state: RootState): Patient | undefined =>
  state.register.patient;

export const selectFeeList = (state: RootState): Fee[] =>
  state.register.feeList;

export function getOutpatientDepartment(): RootThunk {
  return api(apiGetOutpatientDepartment(), (data: any, dispatch) => {
    const departments = _.chain(data.records)
      .map((d) => D.fromJson(d))
      .value();
    dispatch(setDepartments(departments));
  });
}

export function getPatientList(keyword: string): RootThunk {
  return api(
    apiGetPatientList({
      param: keyword.length === 0 ? undefined : keyword,
      size: DefaultPageSize,
      current: 1,
    }),
    (data: any, dispatch) => {
      const patients = _.chain(data.records)
        .map((p) => P.fromJson(p))
        .value();
      dispatch(setPatients(patients));
    }
  );
}

export function getPatientDetail(patientId: string): RootThunk {
  return api(apiGetPatientDetail(patientId), (data: any, dispatch) => {
    dispatch(setPatient(P.fromJson(data)));
  });
}

export function getDoctorsInDepartment(departmentId: string): RootThunk {
  return api(apiGetDoctorsInDepartment(departmentId), (data: any, dispatch) => {
    const doctors = _.chain(data.records)
      .map((u) => U.fromJson(u))
      .value();
    dispatch(setDoctors(doctors));
  });
}

export function getFeeList(): RootThunk {
  return api(apiGetRegistrationFeeList(), (data: any, dispatch) => {
    const feeList = _.chain(data.records)
      .map((f) => F.fromJson(f))
      .value();
    dispatch(setRegistrationFeeList(feeList));
  });
}

export default registerSlice.reducer;
